import React, { memo, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import KnowledgeGraphForm from 'studio/forms/KnowledgeGraphForm';
import services from 'services';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import { paths as studioPaths } from 'studio/App/Studio/Studio';
import { useCreateDevKg } from '../../components/CreateDevKg/CreateDevKg';

const EditKnowledgeGraph = ({ languages, knowledgeGraph, orgSettings, history, fetchKnowledgeGraphs, user }) => {
  const { successMessage, errorMessage } = useMessage();
  const { withLoader } = useLoader();
  const { showConfirmationModal } = useConfirmationModal();

  const updateKnowledgeGraph = useCallback(
    async data => {
      const payload = { ...data, id: knowledgeGraph.data.id };

      const result = await withLoader(services.updateKnowledgeGraph(knowledgeGraph.data.id, payload));
      if (result.success) {
        successMessage('The knowledge graph has successfully been edited');
      } else {
        const message = result?.response?.data?.data?.message || 'There was an error editing the knowledge graph';
        errorMessage(message);
      }
      return result;
    },
    [withLoader, successMessage, errorMessage, knowledgeGraph]
  );

  const deleteKnowledgeGraphById = useCallback(async () => {
    if (knowledgeGraph.data && knowledgeGraph.data.id) {
      const result = await withLoader(services.deleteKnowledgeGraph(knowledgeGraph.data.id));
      if (result.success) {
        successMessage('The knowledge graph has successfully been deleted');
        history.push(studioPaths.root);
        return;
      }
      const message = result?.response?.data?.data?.message || 'There was an error deleting the knowledge graph';
      errorMessage(message);
    }
  }, [knowledgeGraph, successMessage, errorMessage, history, withLoader]);

  const { createDevKnowledgeGraph, CreateDevKgModal } = useCreateDevKg(knowledgeGraph, fetchKnowledgeGraphs, user);

  const formattedLanguages = useMemo(() => {
    if (languages && languages.length) {
      return languages.map(language => ({
        name: language.data.name,
        value: language.data.id
      }));
    }
    return [];
  }, [languages]);

  return (
    <>
      <KnowledgeGraphForm
        knowledgeGraph={knowledgeGraph}
        orgSettings={orgSettings}
        languages={formattedLanguages}
        updateKnowledgeGraph={updateKnowledgeGraph}
        deleteKnowledgeGraph={() =>
          showConfirmationModal(() => ({
            title: knowledgeGraph?.data?.developmentKgForId ? 'Delete Branch' : 'Delete Knowledge Graph',
            content: knowledgeGraph?.data?.developmentKgForId
              ? `Are you sure you want to delete the branch "${
                  knowledgeGraph?.data?.info?.[knowledgeGraph?.data?.defaultLanguageId]?.name ||
                  knowledgeGraph?.data?.id
                }"?`
              : `Are you sure you want to delete the Knowledge Graph "${
                  knowledgeGraph?.data?.info?.[knowledgeGraph?.data?.defaultLanguageId]?.name ||
                  knowledgeGraph?.data?.id
                }"?`,
            onConfirm: deleteKnowledgeGraphById
          }))
        }
        createDevKnowledgeGraph={createDevKnowledgeGraph}
        fetchKnowledgeGraphs={fetchKnowledgeGraphs}
      />
      {CreateDevKgModal()}
    </>
  );
};

export default memo(withRouter(EditKnowledgeGraph));
